import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Button, Chip, Paper, Tooltip, Typography } from '@mui/material';
import { useAxiosClient } from '../utils/useAxiosClient';

interface CollectionRequest {
  id: number;
  collector: string;
  status: string;
  requestedAt?: string | null;
  startedAt?: string | null;
  heartbeatAt?: string | null;
  completedAt?: string | null;
  accountsChangedAt?: string | null;
  notBefore?: string | null;
  attempts?: number | null;
  message?: string | null;
}

interface ActivityRun {
  flowRunId: string;
  label: string;
  isNightly: boolean;
  organizationId?: number | null;
  organizationName?: string | null;
  state: string;
  startTime?: string | null;
  expectedStartTime?: string | null;
}

interface QueueEntry {
  id: number;
  organizationId: number;
  organizationName?: string | null;
  collector: string;
  status: string;
}

interface Activity {
  machine: { online: boolean; lastTickAt?: string | null; prefectOk?: boolean | null; message?: string | null };
  running: ActivityRun[];
  nextNightly?: ActivityRun | null;
  queue: QueueEntry[];
}

interface Props {
  orgId: number | string;
  /** Changes whenever the org's social accounts change, so the request state is re-read. */
  refreshKey?: unknown;
  hasAccounts: boolean;
  onNotify: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Queued',
  running: 'Collecting',
  stop_requested: 'Pausing for nightly',
  paused: 'Paused',
  awaiting_ready: 'Accounts or settings changed',
  success: 'Done',
  partial: 'Done with failures',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  pending: 'info',
  running: 'info',
  stop_requested: 'warning',
  paused: 'warning',
  awaiting_ready: 'warning',
  success: 'success',
  partial: 'warning',
  failed: 'error',
  cancelled: 'default',
};

const COLLECTOR_LABELS: Record<string, string> = {
  main: 'Facebook / TikTok / Twitter / YouTube',
  instagram: 'Instagram',
};

const POLL_MS = 15000;

/**
 * "Ready for collection": the admin signals they are done entering accounts, which queues a one-off
 * collection for this org (picked up by the collection machine within ~2 minutes, never during the
 * nightly run). Editing an account while a one-off is running parks it until this is pressed again.
 */
const ReadyForCollection: React.FC<Props> = ({ orgId, refreshKey, hasAccounts, onNotify }) => {
  const axiosClient = useAxiosClient();
  const [openRequests, setOpenRequests] = useState<CollectionRequest[]>([]);
  const [recentRequests, setRecentRequests] = useState<CollectionRequest[]>([]);
  const [activity, setActivity] = useState<Activity | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axiosClient.get(`/api/admin/organizations/${orgId}/collection-requests`);
      setOpenRequests(res.data.open || []);
      setRecentRequests(res.data.recent || []);
    } catch (e) {
      console.error('Error fetching collection requests:', e);
    }
    try {
      const res = await axiosClient.get('/api/admin/collection-activity');
      setActivity(res.data);
    } catch (e) {
      console.error('Error fetching collection activity:', e);
    }
  }, [axiosClient, orgId]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests, refreshKey]);

  // Poll only while something is open.
  useEffect(() => {
    if (openRequests.length === 0) return undefined;
    timer.current = setInterval(fetchRequests, POLL_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [openRequests.length, fetchRequests]);

  const needsUpdate = openRequests.some((r) => r.status === 'awaiting_ready' || !!r.accountsChangedAt);
  const hasOpen = openRequests.length > 0;

  const handleReady = async () => {
    setBusy(true);
    try {
      const res = await axiosClient.post(`/api/admin/organizations/${orgId}/ready-for-collection`);
      const actions: string[] = (res.data.results || []).map((r: { action: string }) => r.action);
      if (actions.includes('queued')) onNotify('Collection queued. It starts within a couple of minutes.', 'success');
      else if (actions.includes('requeued') || actions.includes('updated')) onNotify('Collection updated with the current accounts.', 'success');
      else onNotify('A collection is already open for this organization.', 'info');
      await fetchRequests();
    } catch (e: any) {
      onNotify(e.response?.data?.detail?.error || e.response?.data?.error || 'Could not queue the collection', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    setBusy(true);
    try {
      await axiosClient.post(`/api/admin/organizations/${orgId}/ready-for-collection/cancel`);
      onNotify('Collection cancelled. A running collector stops after its current chunk.', 'info');
      await fetchRequests();
    } catch (e: any) {
      onNotify(e.response?.data?.detail?.error || 'Could not cancel', 'error');
    } finally {
      setBusy(false);
    }
  };

  const buttonLabel = needsUpdate ? 'Update collection' : hasOpen ? 'Collection in progress' : 'Ready for collection';
  const lastClosed = recentRequests[0];

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Tooltip title={hasAccounts ? '' : 'Add at least one social account first'}>
          <span>
            <Button
              variant="contained"
              color={needsUpdate ? 'warning' : 'primary'}
              onClick={handleReady}
              disabled={busy || !hasAccounts || (hasOpen && !needsUpdate)}
            >
              {buttonLabel}
            </Button>
          </span>
        </Tooltip>
        {hasOpen && (
          <Button variant="text" color="inherit" onClick={handleCancel} disabled={busy}>
            Cancel
          </Button>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 260 }}>
          {needsUpdate
            ? 'Accounts or collection settings changed since collection started. Press "Update collection" when you are done editing.'
            : hasOpen
            ? 'Collecting now. It pauses for the nightly run (1:45 AM ET) and the nightly finishes whatever is left, after all other organizations.'
            : 'Press when you are done entering accounts to start collecting now instead of waiting for the nightly run.'}
        </Typography>
      </Box>

      {openRequests.map((r) => (
        <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          <Chip size="small" color={STATUS_COLORS[r.status] || 'default'} label={STATUS_LABELS[r.status] || r.status} />
          <Typography variant="body2">{COLLECTOR_LABELS[r.collector] || r.collector}</Typography>
          <Typography variant="caption" color="text.secondary">
            {r.message ||
              (r.status === 'running' && r.heartbeatAt ? `last progress ${new Date(r.heartbeatAt).toLocaleTimeString()}` : '') ||
              (r.status === 'pending' ? 'waiting for the collection machine' : '')}
            {r.notBefore ? ` (resumes after ${new Date(r.notBefore).toLocaleString()})` : ''}
          </Typography>
        </Box>
      ))}

      {activity && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          {!activity.machine.online ? (
            <Typography variant="body2" color="error">
              The collection machine is not reporting
              {activity.machine.lastTickAt ? ` (last seen ${new Date(activity.machine.lastTickAt).toLocaleString()})` : ''}.
              Requests stay queued until it is back.
              {activity.machine.message ? ` ${activity.machine.message}` : ''}
            </Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" component="div">
                Running now on the collection machine:{' '}
                {activity.running.length === 0
                  ? 'nothing'
                  : activity.running
                      .map((run) => `${run.label}${run.organizationName ? ` (${run.organizationName})` : ''}${run.startTime ? ` since ${new Date(run.startTime).toLocaleTimeString()}` : ''}`)
                      .join('; ')}
              </Typography>
              {activity.running.some((run) => run.isNightly) && (
                <Typography variant="caption" color="warning.main" component="div">
                  The nightly run has priority. One-off collections wait until it finishes, and the nightly collects
                  organizations that are still backfilling last.
                </Typography>
              )}
              {(() => {
                const ahead = activity.queue.filter(
                  (q) => String(q.organizationId) !== String(orgId) && ['pending', 'running', 'stop_requested', 'paused'].includes(q.status),
                );
                const mine = activity.queue.filter((q) => String(q.organizationId) === String(orgId) && ['pending', 'paused'].includes(q.status));
                const aheadOfMine = mine.length
                  ? ahead.filter((q) => mine.some((m) => m.collector === q.collector && q.id < m.id))
                  : ahead;
                if (aheadOfMine.length === 0) return null;
                const names = Array.from(new Set(aheadOfMine.map((q) => q.organizationName || String(q.organizationId))));
                return (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {mine.length ? 'Ahead of this organization in the queue' : 'Already in the one-off queue'}: {names.join(', ')}.
                    One organization is collected at a time per collector.
                  </Typography>
                );
              })()}
              {activity.nextNightly?.expectedStartTime && (
                <Typography variant="caption" color="text.secondary" component="div">
                  Next nightly run: {new Date(activity.nextNightly.expectedStartTime).toLocaleString()} (one-offs pause 15 minutes before).
                </Typography>
              )}
            </>
          )}
        </Box>
      )}

      {!hasOpen && lastClosed && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">Last one-off:</Typography>
          <Chip size="small" color={STATUS_COLORS[lastClosed.status] || 'default'} label={STATUS_LABELS[lastClosed.status] || lastClosed.status} />
          <Typography variant="caption" color="text.secondary">
            {COLLECTOR_LABELS[lastClosed.collector] || lastClosed.collector}
            {lastClosed.completedAt ? `, ${new Date(lastClosed.completedAt).toLocaleString()}` : ''}
            {lastClosed.message ? ` - ${lastClosed.message}` : ''}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ReadyForCollection;
